import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorToast, successToast } from "../../../utils/toast";
import { superAdminApi } from "../../../services/superAdmin/api";

const formatDateTime = (value) =>
  value
    ? new Intl.DateTimeFormat("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "-";

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    className={`relative inline-flex h-7 w-14 items-center rounded-full transition ${
      checked ? "bg-green-500" : "bg-gray-300"
    }`}
    aria-pressed={checked}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
        checked ? "translate-x-8" : "translate-x-1"
      }`}
    />
    <span
      className={`absolute text-[10px] font-semibold text-white ${
        checked ? "left-2" : "right-2"
      }`}
    >
      {checked ? "ON" : "OFF"}
    </span>
  </button>
);

const SummaryCard = ({ label, value }) => (
  <div className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
    <p className="text-sm font-medium text-gray-500">{label}</p>
    <h3 className="mt-2 text-3xl font-bold text-gray-900">{value}</h3>
  </div>
);

const FeatureManagement = () => {
  const queryClient = useQueryClient();
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [localFeatureFlags, setLocalFeatureFlags] = useState({});

  const tenantsQuery = useQuery({
    queryKey: ["super-admin", "tenants"],
    queryFn: async () => {
      const response = await superAdminApi.getTenants();
      return response.result.items;
    },
  });

  useEffect(() => {
    if (selectedTenantId || !tenantsQuery.data?.length) {
      return;
    }

    setSelectedTenantId(tenantsQuery.data[0].id);
  }, [selectedTenantId, tenantsQuery.data]);

  const featureQuery = useQuery({
    queryKey: ["super-admin", "tenant-features", selectedTenantId],
    queryFn: async () => {
      const response = await superAdminApi.getTenantFeatures(selectedTenantId);
      return response.result;
    },
    enabled: Boolean(selectedTenantId),
  });

  useEffect(() => {
    if (!featureQuery.data?.featureFlags) {
      return;
    }

    setLocalFeatureFlags(featureQuery.data.featureFlags);
  }, [featureQuery.data]);

  const saveFeatureMutation = useMutation({
    mutationFn: (payload) =>
      superAdminApi.updateTenantFeatures(selectedTenantId, payload),
    onSuccess: (response) => {
      successToast(response.message);
      queryClient.invalidateQueries({
        queryKey: ["super-admin", "tenants"],
      });
      queryClient.invalidateQueries({
        queryKey: ["super-admin", "tenant-features", selectedTenantId],
      });
    },
    onError: (error) => {
      errorToast(error?.response?.data?.message || error?.message || "Unable to update feature settings.");
    },
  });

  const featureGroups = useMemo(() => {
    const catalog = featureQuery.data?.featureCatalog || [];

    return catalog.reduce((accumulator, feature) => {
      if (!accumulator[feature.category]) {
        accumulator[feature.category] = [];
      }

      accumulator[feature.category].push(feature);
      return accumulator;
    }, {});
  }, [featureQuery.data]);

  const enabledCount = useMemo(
    () => Object.values(localFeatureFlags).filter(Boolean).length,
    [localFeatureFlags],
  );

  const totalFeatureCount =
    featureQuery.data?.totalCount ||
    Object.keys(localFeatureFlags).length;

  const hasChanges =
    JSON.stringify(localFeatureFlags) !==
    JSON.stringify(featureQuery.data?.featureFlags || {});

  const handleToggle = (featureKey) => {
    setLocalFeatureFlags((currentFlags) => ({
      ...currentFlags,
      [featureKey]: !currentFlags[featureKey],
    }));
  };

  const applyBulkState = (nextValue) => {
    const currentCatalog = featureQuery.data?.featureCatalog || [];
    const nextFlags = currentCatalog.reduce(
      (accumulator, feature) => ({
        ...accumulator,
        [feature.key]: nextValue,
      }),
      {},
    );

    setLocalFeatureFlags(nextFlags);
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 rounded-[28px] border border-orange-100 bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-purple">
            Super Admin
          </p>
          <h1 className="mt-2 text-3xl font-bold text-gray-900">Feature Management</h1>
          <p className="mt-2 max-w-3xl text-sm text-gray-600">
            Maintain centralized tenant-level feature flags using the live backend
            for each tenant/app/institute assignment.
          </p>
        </div>

        <button
          type="button"
          onClick={() => saveFeatureMutation.mutate(localFeatureFlags)}
          disabled={!selectedTenantId || !hasChanges || saveFeatureMutation.isPending}
          className="rounded-2xl bg-bgBlue px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saveFeatureMutation.isPending ? "Saving..." : "Save Feature Settings"}
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <SummaryCard label="Tenants" value={tenantsQuery.data?.length || 0} />
        <SummaryCard label="Enabled Modules" value={enabledCount} />
        <SummaryCard label="Disabled Modules" value={Math.max(totalFeatureCount - enabledCount, 0)} />
      </div>

      <div className="rounded-[28px] border border-orange-100 bg-white p-6 shadow-sm">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto_auto] lg:items-end">
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700">
              Tenant/App/Institute
            </label>
            <select
              value={selectedTenantId}
              onChange={(event) => setSelectedTenantId(event.target.value)}
              className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-bgBlue"
            >
              <option value="">Select tenant</option>
              {(tenantsQuery.data || []).map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.tenantName} | {tenant.appName} | {tenant.instituteName}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={() => applyBulkState(true)}
            disabled={!featureQuery.data}
            className="rounded-2xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:border-bgBlue hover:text-bgBlue disabled:cursor-not-allowed disabled:opacity-50"
          >
            Enable All
          </button>

          <button
            type="button"
            onClick={() => applyBulkState(false)}
            disabled={!featureQuery.data}
            className="rounded-2xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:border-bgBlue hover:text-bgBlue disabled:cursor-not-allowed disabled:opacity-50"
          >
            Disable All
          </button>
        </div>

        {featureQuery.data?.tenant && (
          <div className="mt-5 rounded-3xl border border-orange-100 bg-orange-50/50 p-5">
            <div className="grid gap-3 md:grid-cols-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                  Tenant
                </p>
                <p className="mt-2 font-semibold text-gray-900">
                  {featureQuery.data.tenant.tenantName}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                  App
                </p>
                <p className="mt-2 font-semibold text-gray-900">
                  {featureQuery.data.tenant.appName}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                  Institute
                </p>
                <p className="mt-2 font-semibold text-gray-900">
                  {featureQuery.data.tenant.instituteName}
                </p>
              </div>
            </div>
            <p className="mt-4 text-xs text-gray-500">
              Last synced: {formatDateTime(featureQuery.data.updatedAt)}
            </p>
          </div>
        )}

        <div className="mt-6">
          {tenantsQuery.isLoading || (selectedTenantId && featureQuery.isLoading) ? (
            <div className="flex min-h-[280px] items-center justify-center rounded-3xl border border-dashed border-orange-200 bg-orange-50/40">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-purple border-t-transparent" />
            </div>
          ) : tenantsQuery.isError || featureQuery.isError ? (
            <div className="rounded-3xl border border-red-100 bg-red-50 p-8 text-center">
              <h2 className="text-xl font-semibold text-red-700">
                Unable to load feature settings
              </h2>
              <p className="mt-2 text-sm text-red-600">
                The feature configuration could not be fetched for the selected
                tenant/app.
              </p>
              <button
                type="button"
                onClick={() => {
                  tenantsQuery.refetch();
                  featureQuery.refetch();
                }}
                className="mt-5 rounded-xl bg-red-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          ) : !selectedTenantId ? (
            <div className="rounded-3xl border border-dashed border-orange-200 bg-orange-50/40 p-10 text-center">
              <h2 className="text-xl font-semibold text-gray-900">
                Select a tenant to manage feature flags
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Feature toggles are scoped per tenant/app/institute mapping.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {Object.entries(featureGroups).map(([category, features]) => (
                <div
                  key={category}
                  className="rounded-3xl border border-gray-100 bg-gray-50/70 p-5"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {category}
                      </h2>
                      <p className="mt-1 text-sm text-gray-500">
                        Centralized feature flags for the {category.toLowerCase()} area.
                      </p>
                    </div>
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-600">
                      {
                        features.filter((feature) => localFeatureFlags[feature.key])
                          .length
                      }
                      /{features.length} enabled
                    </span>
                  </div>

                  <div className="mt-4 grid gap-4 xl:grid-cols-2">
                    {features.map((feature) => (
                      <div
                        key={feature.key}
                        className="flex items-start justify-between gap-4 rounded-3xl border border-white bg-white p-5 shadow-sm"
                      >
                        <div>
                          <p className="font-semibold text-gray-900">
                            {feature.label}
                          </p>
                          <p className="mt-2 text-sm text-gray-600">
                            {feature.description}
                          </p>
                          <p className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-gray-400">
                            Key: {feature.key}
                          </p>
                        </div>

                        <Toggle
                          checked={Boolean(localFeatureFlags[feature.key])}
                          onChange={() => handleToggle(feature.key)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeatureManagement;
